import type { Theme } from "@mui/material/styles";
import { color } from "./tokens/color.js";

/**
 * D91's chip recipe (superseding D81's label rule), in one place.
 *
 * Before D81 the same colour-band `switch` was copy-pasted into six files, each
 * with its own hardcoded `rgba()` fills and its own darkened foreground hex.
 * D81 collapsed them here and set the rule: the hue owns the FILL, the DOT and
 * the KEYLINE; the LABEL is neutral. It did so because a status colour as its
 * own label measured below AA on its own tint — critical 3.70:1 on `--ink-700`
 * — and no single alpha fixed every slot.
 *
 * D91 (2026-09-13, Christian, platform owner) changes what the LABEL is and
 * retires the keyline:
 *
 *   THE HUE IS THE LABEL. "The original MUI style — yellow background, yellow
 *   font." The label is the slot's hue, lifted toward the ground's opposite
 *   ONLY as far as 4.5:1 on the fill requires and not one step further, so the
 *   hue stays the hue. On dark that is a 4% tint toward white for critical and
 *   neutral and nothing for the other three; on light it is a darkening. The
 *   lift is computed, not tuned by hand, so a token change cannot silently
 *   drop a label under the floor — `contrast.test.ts` re-measures every slot
 *   on every ground in both modes.
 *
 *   NO KEYLINE. D81 added the keyline as a third signal beside fill and dot
 *   because the label carried no hue. With the hue in the label the chip
 *   already carries two hue signals plus its text; the keyline read as a third
 *   outline on every list page ("the tags are too dominant") and is gone.
 *   `--status-keyline-alpha` stays vendored and unused by chips.
 *
 * What D81 keeps: the five MEANING-named slots, the 18% fill alpha, the dot
 * where a surface draws one (StatusPill), the bar on inventing a sixth colour
 * at a call site, and the discipline that every ratio here is measured.
 */
export type StatusSlot =
  | "neutral" // inert · not started
  | "info" // informational · running
  | "success" // completed cleanly
  | "attention" // needs a human
  | "critical"; // failed · blocking

export interface StatusChipStyle {
  backgroundColor: string;
  /** The hue, lifted only as far as AA on `backgroundColor` requires. */
  color: string;
  /** The hue itself, unlifted — for the dot, and for any accent that sits on
   *  the page rather than on the chip's own fill. */
  dotColor: string;
}

/**
 * Resolves a slot to the three values the recipe needs, in the theme's mode.
 *
 * `color-mix()` is deliberately not used: the values have to be real so
 * `contrast.test.ts` can measure them, and so a Konva canvas or a raw-DOM map
 * marker — neither of which CSS can reach — gets the same colour as a chip.
 */
export function statusChipStyle(
  theme: Theme,
  slot: StatusSlot,
): StatusChipStyle {
  const mode = theme.palette.mode;
  const c = color[mode];
  const hue = c.status[slot];
  const fill = alphaOver(hue, c.chip.fillAlpha, c.surface.default);
  return {
    backgroundColor: fill,
    color: labelOnFill(hue, fill),
    dotColor: hue,
  };
}

const AA_TEXT = 4.5;

/**
 * The hue as a label on its own fill: moved toward white or toward black —
 * whichever gets there sooner — in 1% steps until it reads 4.5:1, and no
 * further. Measured against the live dark tokens: critical `#F0655C` →
 * `#F16B63` and neutral `#88959B` → `#8D999F` (4% each); attention, success
 * and info already clear the floor and come back unchanged.
 */
export function labelOnFill(hue: string, fill: string): string {
  const h = parseHex(hue);
  const f = parseHex(fill);
  if (contrastRatio(h, f) >= AA_TEXT) return hue;
  for (let t = 0.01; t <= 1; t += 0.01) {
    for (const toward of [255, 0]) {
      const mixed: Rgb = [
        toward * t + h[0] * (1 - t),
        toward * t + h[1] * (1 - t),
        toward * t + h[2] * (1 - t),
      ];
      if (contrastRatio(mixed, f) >= AA_TEXT) return toHex(mixed);
    }
  }
  return "#ffffff";
}

type Rgb = readonly [number, number, number];

function parseHex(hex: string): Rgb {
  const m = hex.replace("#", "").match(/../g) ?? [];
  return [
    parseInt(m[0] ?? "0", 16),
    parseInt(m[1] ?? "0", 16),
    parseInt(m[2] ?? "0", 16),
  ];
}

function toHex(rgb: Rgb): string {
  return `#${rgb
    .map((v) =>
      Math.max(0, Math.min(255, Math.round(v)))
        .toString(16)
        .padStart(2, "0"),
    )
    .join("")}`;
}

function luminance([r, g, b]: Rgb): number {
  const lin = (v: number): number => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/** WCAG 2.x contrast ratio between two colours. */
export function contrastRatio(a: Rgb, b: Rgb): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/**
 * Flattens an alpha tint over a ground into a real colour.
 *
 * Composited rather than left as `rgba()` so the result is a value the contrast
 * test can measure and a canvas can consume. An `rgba()` fill measures nothing
 * on its own — which is exactly how the previous pattern's contrast claims went
 * unchecked for as long as they did.
 */
export function alphaOver(hex: string, alpha: number, ground: string): string {
  const parse = (s: string): number[] =>
    (s.replace("#", "").match(/../g) ?? []).map((p) => parseInt(p, 16));
  const fg = parse(hex);
  const bg = parse(ground);
  const mix = (i: number): number =>
    Math.round((fg[i] ?? 0) * alpha + (bg[i] ?? 0) * (1 - alpha));
  return `#${[0, 1, 2]
    .map((i) => mix(i).toString(16).padStart(2, "0"))
    .join("")}`;
}
